/*
  Summit Feedback admin page.
  Manage summit years and their workshops/sessions, capture (lock) a year,
  and open results. Prior years are listed under Archive, newest first.
*/
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, post, patch, getErrorMessage } from '../api/client';

const emptyWorkshop = () => ({ name: '', sessionNumber: '', sessionDate: '', speaker: '' });

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const inputStyle = {
  padding: '0.4rem 0.5rem',
  borderRadius: '6px',
  border: '1px solid var(--cr-border)',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
};

const WorkshopRows = ({ workshops, onChange }) => {
  const update = (i, field, value) => {
    const next = workshops.map((w, idx) => (idx === i ? { ...w, [field]: value } : w));
    onChange(next);
  };
  const remove = (i) => onChange(workshops.filter((_, idx) => idx !== i));
  return (
    <div>
      {workshops.map((w, i) => (
        <div key={w._id || `new-${i}`} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem', alignItems: 'center' }}>
          <input
            aria-label={`Workshop ${i + 1} session number`}
            type="number"
            min="0"
            placeholder="#"
            value={w.sessionNumber}
            onChange={(e) => update(i, 'sessionNumber', e.target.value)}
            style={{ ...inputStyle, width: '3.5rem' }}
            required
          />
          <input
            aria-label={`Workshop ${i + 1} name`}
            type="text"
            placeholder="Workshop / session name"
            value={w.name}
            onChange={(e) => update(i, 'name', e.target.value)}
            style={{ ...inputStyle, flex: '2 1 180px', minWidth: 0 }}
            required
          />
          <input
            aria-label={`Workshop ${i + 1} speaker`}
            type="text"
            placeholder="Speaker (optional)"
            value={w.speaker}
            onChange={(e) => update(i, 'speaker', e.target.value)}
            style={{ ...inputStyle, flex: '1 1 120px', minWidth: 0 }}
          />
          <input
            aria-label={`Workshop ${i + 1} date`}
            type="date"
            value={w.sessionDate}
            onChange={(e) => update(i, 'sessionDate', e.target.value)}
            style={{ ...inputStyle, width: '9.5rem' }}
          />
          <button type="button" className="btn-outline" onClick={() => remove(i)} aria-label={`Remove workshop ${i + 1}`} style={{ padding: '0.3rem 0.6rem' }}>
            ✕
          </button>
        </div>
      ))}
      <button type="button" className="btn-outline" onClick={() => onChange([...workshops, emptyWorkshop()])}>
        + Add workshop
      </button>
    </div>
  );
};

const SummitFeedbackAdminPage = () => {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newYear, setNewYear] = useState(String(new Date().getFullYear()));
  const [newTitle, setNewTitle] = useState('');
  const [newWorkshops, setNewWorkshops] = useState([emptyWorkshop()]);
  const [creating, setCreating] = useState(false);

  // Edit workshops of the open year
  const [editingYearId, setEditingYearId] = useState(null);
  const [editWorkshops, setEditWorkshops] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { ok, status, data } = await get('/api/summit-feedback/years');
    if (ok) setYears(Array.isArray(data) ? data : []);
    else setError(getErrorMessage({ status, data }, 'Failed to load summit years'));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const normalizeWorkshops = (list) =>
    list
      .filter((w) => w.name.trim() || String(w.sessionNumber).trim())
      .map((w) => ({
        ...(w._id ? { _id: w._id } : {}),
        name: w.name.trim(),
        sessionNumber: Number(w.sessionNumber),
        sessionDate: w.sessionDate || undefined,
        speaker: w.speaker?.trim() || '',
      }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setNotice('');
    const { ok, status, data } = await post('/api/summit-feedback/years', {
      year: Number(newYear),
      title: newTitle.trim() || undefined,
      workshops: normalizeWorkshops(newWorkshops),
    });
    setCreating(false);
    if (ok) {
      setShowCreate(false);
      setNewTitle('');
      setNewWorkshops([emptyWorkshop()]);
      setNotice(`Summit ${data.year} created.`);
      load();
    } else {
      setError(getErrorMessage({ status, data }, 'Failed to create summit year'));
    }
  };

  const startEdit = (year) => {
    setEditingYearId(year._id);
    setEditWorkshops(
      (year.workshops || []).map((w) => ({
        _id: w._id,
        name: w.name,
        sessionNumber: String(w.sessionNumber),
        sessionDate: toDateInput(w.sessionDate),
        speaker: w.speaker || '',
      }))
    );
    setError('');
    setNotice('');
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    setError('');
    const { ok, status, data } = await patch(`/api/summit-feedback/years/${editingYearId}`, {
      workshops: normalizeWorkshops(editWorkshops),
    });
    setSavingEdit(false);
    if (ok) {
      setEditingYearId(null);
      setNotice('Workshops updated.');
      load();
    } else {
      setError(getErrorMessage({ status, data }, 'Failed to update workshops'));
    }
  };

  const captureYear = async (year) => {
    const confirmed = window.confirm(
      `Capture feedback for ${year.title}? This locks ALL responses for ${year.year} — attendees will no longer be able to add or edit feedback.`
    );
    if (!confirmed) return;
    setError('');
    const { ok, status, data } = await post(`/api/summit-feedback/years/${year._id}/capture`, {});
    if (ok) {
      setNotice(`${year.title} captured. Responses are now locked.`);
      load();
    } else {
      setError(getErrorMessage({ status, data }, 'Failed to capture year'));
    }
  };

  const reopenYear = async (year) => {
    const confirmed = window.confirm(`Reopen ${year.title}? Attendees will be able to edit feedback again.`);
    if (!confirmed) return;
    setError('');
    const { ok, status, data } = await post(`/api/summit-feedback/years/${year._id}/reopen`, {});
    if (ok) {
      setNotice(`${year.title} reopened.`);
      load();
    } else {
      setError(getErrorMessage({ status, data }, 'Failed to reopen year'));
    }
  };

  const renderYearCard = (year, { archived }) => (
    <div
      key={year._id}
      style={{
        background: 'var(--cr-bg-warm)',
        border: archived ? '1px solid var(--cr-border)' : '2px solid var(--cr-primary, #4a6741)',
        borderRadius: '10px',
        padding: '1rem 1.15rem',
        marginBottom: '0.85rem',
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', flex: '1 1 auto' }}>{year.title}</h3>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, borderRadius: '999px', padding: '0.15rem 0.6rem', background: year.status === 'open' ? 'var(--cr-success, #2e7d32)' : 'var(--cr-border)', color: year.status === 'open' ? '#fff' : 'var(--cr-text)' }}>
          {year.status === 'open' ? 'Open' : 'Captured'}
        </span>
      </div>
      <p style={{ margin: '0.35rem 0 0.75rem', color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>
        {(year.workshops || []).length} workshops · {year.submittedCount} submitted response{year.submittedCount === 1 ? '' : 's'}
        {year.capturedAt ? ` · captured ${new Date(year.capturedAt).toLocaleDateString()}` : ''}
      </p>

      {editingYearId === year._id ? (
        <div style={{ marginBottom: '0.75rem' }}>
          <WorkshopRows workshops={editWorkshops} onChange={setEditWorkshops} />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button type="button" className="btn-primary" onClick={saveEdit} disabled={savingEdit}>
              {savingEdit ? 'Saving…' : 'Save workshops'}
            </button>
            <button type="button" className="btn-outline" onClick={() => setEditingYearId(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          <Link to={`/admin/summit-feedback/${year._id}/results`} className="btn-primary" style={{ textDecoration: 'none' }}>
            View results
          </Link>
          {year.status === 'open' && (
            <>
              <button type="button" className="btn-outline" onClick={() => startEdit(year)}>Edit workshops</button>
              <button type="button" className="btn-outline" onClick={() => captureYear(year)}>Capture feedback</button>
            </>
          )}
          {year.status === 'captured' && (
            <button type="button" className="btn-outline" onClick={() => reopenYear(year)}>Reopen</button>
          )}
        </div>
      )}
    </div>
  );

  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;

  const openYears = years.filter((y) => y.status === 'open');
  const archivedYears = years.filter((y) => y.status !== 'open');

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1rem 6rem 1rem' }}>
      <PageHeader title="Summit Feedback" section="leaders" />
      <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.95rem', marginBottom: '1.25rem' }}>
        Manage the yearly CR Summit feedback: create the year with its workshops and sessions,
        collect feedback from attendees (users with the <code>summit-feedback</code> permission),
        then capture it to lock responses and generate results.
      </p>

      {notice && <p style={{ color: 'var(--cr-text)', fontWeight: 500 }}>{notice}</p>}
      {error && <p style={{ color: 'var(--cr-error)' }}>{error}</p>}

      {openYears.map((y) => renderYearCard(y, { archived: false }))}

      {openYears.length === 0 && !showCreate && (
        <p style={{ color: 'var(--cr-text-muted)' }}>No summit year is open right now.</p>
      )}

      {!showCreate ? (
        <button
          type="button"
          className="btn-primary"
          onClick={() => { setShowCreate(true); setNotice(''); setError(''); }}
          disabled={openYears.length > 0}
          title={openYears.length > 0 ? 'Capture the open year before creating a new one' : undefined}
          style={{ marginBottom: '1.5rem' }}
        >
          + New summit year
        </button>
      ) : (
        <form onSubmit={handleCreate} style={{ background: 'var(--cr-bg-warm)', border: '1px solid var(--cr-border)', borderRadius: '10px', padding: '1rem 1.15rem', marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>New summit year</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.9rem' }}>
              Year
              <input type="number" min="2000" max="2100" value={newYear} onChange={(e) => setNewYear(e.target.value)} style={{ ...inputStyle, width: '6rem' }} required />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.9rem', flex: '1 1 200px' }}>
              Title (optional)
              <input type="text" placeholder={`Summit ${newYear}`} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} style={inputStyle} />
            </label>
          </div>
          <p style={{ fontSize: '0.9rem', fontWeight: 600, margin: '0 0 0.4rem' }}>Workshops &amp; sessions</p>
          <WorkshopRows workshops={newWorkshops} onChange={setNewWorkshops} />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="submit" className="btn-primary" disabled={creating}>
              {creating ? 'Creating…' : 'Create year'}
            </button>
            <button type="button" className="btn-outline" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </form>
      )}

      {archivedYears.length > 0 && (
        <>
          <h2 style={{ fontSize: '1.05rem', color: 'var(--cr-text-muted)', borderTop: '1px solid var(--cr-border)', paddingTop: '1.25rem' }}>Archive</h2>
          {archivedYears.map((y) => renderYearCard(y, { archived: true }))}
        </>
      )}
    </div>
  );
};

export default SummitFeedbackAdminPage;
